import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import Heading from '@cotyorg/ccx-atoms/Heading';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import ProductSwatchColours from '@cotyorg/ccx-organism-product-swatch/components/ProductSwatchColours';
import ProductSwatchDropdown from '@cotyorg/ccx-organism-product-swatch/components/ProductSwatchDropdown';
import ProductDetailsAccordion from '@cotyorg/ccx-molecules/ProductDetails/components/ProductDetailsAccordion';
import classNames from 'classnames';
import { trackSelectedShadeEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/product';
import ProductRetailers from '../../../ProductRetailers';
import { isSmallBreakpoint } from '../../../../utils';
import ChannelAdvisorWidget from '../../../../molecules/ChannelAdvisorWidget';

const generateProductSwatchesClassNames = ({ isOpen }) =>
  classNames('product-info__swatches', {
    'product-info__swatches--is-open': isOpen,
  });

const ProductInfo = ({
  sectionHeadings,
  productDetails,
  currentBreakpoint,
  categoryTitle,
  subcategoryTitle,
  productSwatch,
  toggleAllShades,
  isOpen,
  showRating = true,
  customRetailer,
  selectedSKU,
  channelAdvisorWidget,
  hidePrice,
  vtoButton,
}) => (
  <div className="product-info">
    <div className="product-info__inner">
      {!isSmallBreakpoint({ currentBreakpoint }) && (
        <>
          <Heading extraClass="product-info__tag-heading" level={4}>
            {R.path(['productTag'], productDetails)}
          </Heading>

          <Heading
            data-test="product-heading"
            extraClass="h2 product-info__heading"
            level={1}
            itemProp="name"
            dangerouslySetInnerHTML={{
              __html: R.path(['productDetails', 'heading'], sectionHeadings),
            }}
          />
        </>
      )}

      <Heading
        level={2}
        extraClass="product-info__description"
        data-test="product-description"
      >
        {R.prop('description', productDetails)}
      </Heading>

      <OptionalSection
        renderCondition={showRating}
        section={() => (
          <div
            data-bv-show="rating_summary"
            data-bv-product-id={R.prop('bvid', productDetails)}
          />
        )}
      />
    </div>

    {R.hasPath(['swatches', 0, 'skuColor'], productSwatch) && (
      <div>
        <div className={generateProductSwatchesClassNames({ isOpen })}>
          {R.prop('shadeSelectLabel', productDetails) && (
            <ProductSwatchColours
              label={R.prop('shadeSelectLabel', productDetails)}
              toggleMoreLabel={R.prop('showAllShadesLabel', productDetails)}
              separator="/ "
              labelFirst
              nameFirst
              onChange={({ shade, sku }) => {
                trackSelectedShadeEvent({
                  shade: R.toLower(shade),
                  sku,
                  name: R.compose(R.toLower(), R.prop('title'))(productDetails),
                  type: R.toLower(subcategoryTitle),
                  location: 'radio button',
                });
              }}
              toggleAllShades={toggleAllShades}
              currentBreakpoint={currentBreakpoint}
            />
          )}
        </div>
      </div>
    )}

    <div className="product-info__swatch-dropdown">
      <ProductSwatchDropdown
        label={R.prop('shadeSelectLabel', productDetails)}
        separator="/ "
        labelFirst
        nameFirst
        chevronIconSrc="/ccx-files/assets/chevron-down.svg"
        onChange={({ shade, sku }) => {
          trackSelectedShadeEvent({
            shade: R.toLower(shade),
            sku,
            name: R.compose(R.toLower(), R.prop('title'))(productDetails),
            type: R.toLower(subcategoryTitle),
          });
        }}
      />
    </div>

    {vtoButton && vtoButton()}

    {R.prop('showWidget', channelAdvisorWidget) ? (
      <span className="buy-now-section">
        <ChannelAdvisorWidget
          selectedSKU={selectedSKU}
          url={R.prop('url', channelAdvisorWidget)}
          lang={R.prop('lang', channelAdvisorWidget)}
          caPid={R.prop('caPid', channelAdvisorWidget)}
        />
      </span>
    ) : (
      <ProductRetailers
        sectionHeadings={sectionHeadings}
        productTitle={R.prop('title', productDetails)}
        categoryTitle={categoryTitle}
        subcategoryTitle={subcategoryTitle}
        productSwatch={productSwatch}
        customRetailer={customRetailer}
        hidePrice={hidePrice}
      />
    )}

    <ProductDetailsAccordion
      minusIconSrc="/ccx-files/assets/chevron-up.svg"
      plusIconSrc="/ccx-files/assets/chevron-down.svg"
    />
  </div>
);

ProductInfo.propTypes = {
  productDetails: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    averageRating: PropTypes.number,
    numberOfReviews: PropTypes.number,
  }),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  currentBreakpoint: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  toggleAllShades: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  showRating: PropTypes.bool,
  customRetailer: PropTypes.shape({
    title: PropTypes.string,
    button: PropTypes.shape({
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    retailer: PropTypes.shape({
      name: PropTypes.string.isRequired,
      currencyCode: PropTypes.string,
      currency: PropTypes.string,
    }),
  }),
  selectedSKU: PropTypes.string.isRequired,
  channelAdvisorWidget: PropTypes.shape({
    url: PropTypes.string.isRequired,
    caPid: PropTypes.string.isRequired,
    lang: PropTypes.string,
  }),
  hidePrice: PropTypes.bool,
  vtoButton: PropTypes.func,
};

export default ProductInfo;
